import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Build as TechIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useTechnologies,
  useCreateTechnology,
  useUpdateTechnology,
  useDeleteTechnology,
} from "../../hooks/useTechnologies";
import {
  adminColors,
  textFieldStyles,
  selectMenuProps,
  dialogStyles,
  tableStyles,
  buttonStyles,
  chipStyles,
  switchStyles,
} from "./adminStyles";
import { useTranslation } from "react-i18next";

const AdminTechnologies = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: technologies, isLoading, error } = useTechnologies();
  const createTechnology = useCreateTechnology();
  const updateTechnology = useUpdateTechnology();
  const deleteTechnology = useDeleteTechnology();

  const categories = [
    {
      value: "frontend",
      label: t("admin.technologiesMeta.categories.frontend"),
      color: "#0FA3A3",
    },
    {
      value: "backend",
      label: t("admin.technologiesMeta.categories.backend"),
      color: "#D4AF37",
    },
    {
      value: "language",
      label: t("admin.technologiesMeta.categories.language"),
      color: "#6366F1",
    },
    {
      value: "database",
      label: t("admin.technologiesMeta.categories.database"),
      color: "#8B5CF6",
    },
    {
      value: "devops",
      label: t("admin.technologiesMeta.categories.devops"),
      color: "#EC4899",
    },
    {
      value: "mobile",
      label: t("admin.technologiesMeta.categories.mobile"),
      color: "#10B981",
    },
    {
      value: "ai_ml",
      label: t("admin.technologiesMeta.categories.aiMl"),
      color: "#F59E0B",
    },
    {
      value: "cloud",
      label: t("admin.technologiesMeta.categories.cloud"),
      color: "#0EA5E9",
    },
    {
      value: "integration",
      label: t("admin.technologiesMeta.categories.integration"),
      color: "#8B5CF6",
    },
    {
      value: "tools",
      label: t("admin.technologiesMeta.categories.tools"),
      color: "#6366F1",
    },
    {
      value: "other",
      label: t("admin.technologiesMeta.categories.other"),
      color: "#9CA3AF",
    },
  ];

  const proficiencyLevels = [
    {
      value: "beginner",
      label: t("admin.technologiesMeta.proficiency.beginner"),
      color: "#9CA3AF",
    },
    {
      value: "intermediate",
      label: t("admin.technologiesMeta.proficiency.intermediate"),
      color: "#F59E0B",
    },
    {
      value: "advanced",
      label: t("admin.technologiesMeta.proficiency.advanced"),
      color: "#10B981",
    },
    {
      value: "expert",
      label: t("admin.technologiesMeta.proficiency.expert"),
      color: "#0FA3A3",
    },
  ];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTech, setSelectedTech] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "frontend",
    proficiency: "intermediate",
    logo_url: "",
    years_experience: 1,
    description: "",
    is_published: true,
  });

  const handleOpenCreate = () => {
    setSelectedTech(null);
    setFormData({
      name: "",
      category: "frontend",
      proficiency: "intermediate",
      logo_url: "",
      years_experience: 1,
      description: "",
      is_published: true,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (tech) => {
    setSelectedTech(tech);
    const initialCategory = categories.find((c) => c.value === tech.category)
      ? tech.category
      : "frontend";
    setFormData({
      name: tech.name || "",
      category: initialCategory,
      proficiency: tech.proficiency || "intermediate",
      logo_url: tech.logo_url || "",
      years_experience: tech.years_experience || 1,
      description: tech.description || "",
      is_published: tech.is_published !== undefined ? tech.is_published : true,
    });
    setDialogOpen(true);
  };

  const handleOpenDelete = (tech) => {
    setSelectedTech(tech);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (selectedTech) {
        await updateTechnology.mutateAsync({
          id: selectedTech.id,
          ...formData,
        });
      } else {
        await createTechnology.mutateAsync(formData);
      }
      setDialogOpen(false);
    } catch (err) {
      console.error("Error saving technology:", err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTechnology.mutateAsync(selectedTech.id);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Error deleting technology:", err);
    }
  };

  const getCategoryInfo = (category) => {
    return (
      categories.find((c) => c.value === category) || {
        label: category,
        color: adminColors.text.secondary,
      }
    );
  };

  const getProficiencyInfo = (proficiency) => {
    return (
      proficiencyLevels.find((p) => p.value === proficiency) || {
        label: proficiency,
        color: adminColors.text.secondary,
      }
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: adminColors.bg.page,
        }}
      >
        <CircularProgress sx={{ color: adminColors.accent.teal }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: "100vh", bgcolor: adminColors.bg.page, pt: 12, pb: 8 }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/admin")}
              sx={{ color: adminColors.text.primary }}
            >
              <BackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{ color: adminColors.text.primary, fontWeight: 700 }}
            >
              {t("admin.sections.technologies")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={buttonStyles.primary}
          >
            {t("admin.actions.create")}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error.message}
          </Alert>
        )}

        <TableContainer component={Paper} sx={tableStyles.container}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.name")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.category")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.level")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.yearsExperience")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.status")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell} align="right">
                  {t("admin.labels.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {technologies?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      ...tableStyles.bodyCell,
                      textAlign: "center",
                      py: 4,
                      color: adminColors.text.secondary,
                    }}
                  >
                    {t("admin.messages.noData")}
                  </TableCell>
                </TableRow>
              )}
              {technologies?.map((tech) => {
                const catInfo = getCategoryInfo(tech.category);
                const profInfo = getProficiencyInfo(tech.proficiency);
                return (
                  <TableRow key={tech.id} sx={tableStyles.row}>
                    <TableCell sx={tableStyles.bodyCell}>{tech.name}</TableCell>
                    <TableCell sx={tableStyles.bodyCell}>
                      <Chip
                        label={catInfo.label}
                        size="small"
                        sx={chipStyles.type(catInfo.color)}
                      />
                    </TableCell>
                    <TableCell sx={tableStyles.bodyCell}>
                      <Chip
                        label={profInfo.label}
                        size="small"
                        sx={chipStyles.type(profInfo.color)}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableStyles.bodyCell,
                        color: adminColors.text.secondary,
                      }}
                    >
                      {tech.years_experience}
                    </TableCell>
                    <TableCell sx={tableStyles.bodyCell}>
                      <Chip
                        label={
                          tech.is_published
                            ? t("admin.labels.published")
                            : t("admin.labels.draft")
                        }
                        size="small"
                        sx={
                          tech.is_published
                            ? chipStyles.published
                            : chipStyles.draft
                        }
                      />
                    </TableCell>
                    <TableCell sx={tableStyles.bodyCell} align="right">
                      <IconButton
                        onClick={() => handleOpenEdit(tech)}
                        sx={buttonStyles.iconEdit}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenDelete(tech)}
                        sx={buttonStyles.iconDelete}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: adminColors.text.primary,
              borderBottom: `1px solid ${adminColors.border.default}`,
              pb: 2,
            }}
          >
            <TechIcon sx={{ color: adminColors.accent.teal }} />
            {selectedTech
              ? `${t("admin.actions.edit")} ${t("admin.sections.technologies")}`
              : `${t("admin.actions.create")} ${t(
                  "admin.sections.technologies"
                )}`}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label={t("admin.labels.name")}
                fullWidth
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                sx={textFieldStyles}
              />
              <TextField
                select
                label={t("admin.labels.category")}
                name="category"
                fullWidth
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                sx={textFieldStyles}
                SelectProps={selectMenuProps}
              >
                {categories.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t("admin.labels.level")}
                fullWidth
                value={formData.proficiency}
                onChange={(e) =>
                  setFormData({ ...formData, proficiency: e.target.value })
                }
                sx={textFieldStyles}
                SelectProps={selectMenuProps}
              >
                {proficiencyLevels.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t("admin.labels.yearsExperience")}
                fullWidth
                type="number"
                value={formData.years_experience}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    years_experience: parseInt(e.target.value) || 1,
                  })
                }
                sx={textFieldStyles}
              />
              <TextField
                label={t("admin.labels.description")}
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                sx={textFieldStyles}
              />
              <TextField
                label="Logo URL"
                fullWidth
                value={formData.logo_url}
                onChange={(e) =>
                  setFormData({ ...formData, logo_url: e.target.value })
                }
                sx={textFieldStyles}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_published}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_published: e.target.checked,
                      })
                    }
                    sx={switchStyles}
                  />
                }
                label={t("admin.labels.status")}
                sx={{ color: adminColors.text.primary }}
              />
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{ p: 3, borderTop: `1px solid ${adminColors.border.default}` }}
          >
            <Button
              onClick={() => setDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={
                createTechnology.isPending || updateTechnology.isPending
              }
              sx={buttonStyles.primary}
            >
              {createTechnology.isPending || updateTechnology.isPending ? (
                <CircularProgress
                  size={24}
                  sx={{ color: adminColors.bg.page }}
                />
              ) : (
                t("admin.actions.save")
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle sx={{ color: adminColors.text.primary }}>
            {t("admin.messages.confirmDeleteTitle")}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: adminColors.text.secondary }}>
              {t("admin.messages.confirmDelete")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              disabled={deleteTechnology.isPending}
              sx={{
                bgcolor: adminColors.status.error,
                "&:hover": { bgcolor: "#DC2626" },
              }}
            >
              {deleteTechnology.isPending ? (
                <CircularProgress size={24} />
              ) : (
                t("admin.actions.delete")
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminTechnologies;
